/**
 * Premium cream/gold confirmation dialog — portaled above activity modals (z-[200200]+).
 */

const DEFAULT_Z = 'z-[200200]';

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {object} options
 * @param {string} options.title
 * @param {string} [options.message]
 * @param {string[]} [options.bullets]
 * @param {string} [options.confirmLabel]
 * @param {string} [options.cancelLabel]
 * @param {'danger'|'primary'} [options.variant]
 * @param {string} [options.zIndexClass]
 * @returns {Promise<boolean>}
 */
export function showPremiumConfirm({
  title,
  message,
  bullets,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  zIndexClass = DEFAULT_Z,
}) {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    container.className = `fixed inset-0 ${zIndexClass} flex items-center justify-center p-4`;
    container.setAttribute('role', 'presentation');

    const bulletsBlock =
      bullets?.length > 0
        ? `<div class="mb-5 rounded-2xl border border-[#F5D99B]/70 bg-[#FFF9EF]/90 px-4 py-3.5 text-left">
            <p class="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">This will permanently remove</p>
            <ul class="list-disc space-y-1 pl-4 text-sm leading-relaxed text-[#5A4632]">
              ${bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}
            </ul>
            <p class="mt-3 text-sm font-semibold text-[#2F251B]">This action cannot be undone.</p>
          </div>`
        : '';

    const messageBlock =
      !bullets?.length && message
        ? `<p class="mb-6 text-sm leading-relaxed text-[#5A4632]">${escapeHtml(message)}</p>`
        : '';

    const confirmClass =
      variant === 'danger'
        ? 'rounded-xl border border-rose-300/80 bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_22px_rgba(190,40,40,0.25)] transition hover:from-rose-600 hover:to-rose-700'
        : 'rounded-xl border border-[#E8C97A] bg-gradient-to-r from-[#fde68a] via-[#fbbf24] to-[#f59e0b] px-5 py-2.5 text-sm font-semibold text-[#4a3728] shadow-[0_8px_22px_rgba(180,120,20,0.2)] transition hover:brightness-105';

    container.innerHTML = `
      <div data-confirm-backdrop class="absolute inset-0 bg-[#2f251b]/35 backdrop-blur-sm" aria-hidden="true"></div>
      <div
        class="relative w-full max-w-lg overflow-hidden rounded-[28px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] shadow-[0_28px_80px_rgba(120,72,20,0.2)] ring-1 ring-[#efe2d0]/80"
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-confirm-title"
      >
        <div class="h-1 w-full shrink-0 bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F97316]" aria-hidden="true"></div>
        <div class="px-6 py-5 sm:px-7 sm:py-6">
          <div class="mb-4 flex items-start gap-3">
            <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#F5D99B]/80 bg-[#FFF9EF] ${
              variant === 'danger' ? 'text-rose-600' : 'text-[#B45309]'
            }">
              ${
                variant === 'danger'
                  ? `<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>`
                  : `<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>`
              }
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">ARTmosphere</p>
              <h3 id="premium-confirm-title" class="font-serif text-xl font-semibold leading-tight text-[#2F251B] sm:text-2xl">${escapeHtml(title)}</h3>
            </div>
          </div>
          ${messageBlock}
          ${bulletsBlock}
          <div class="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end sm:gap-3">
            <button type="button" data-confirm-cancel class="rounded-xl border border-[#e8d4bc] bg-white px-5 py-2.5 text-sm font-semibold text-[#5A4632] shadow-sm transition hover:bg-[#fffdf9]">${escapeHtml(cancelLabel)}</button>
            <button type="button" data-confirm-ok class="${confirmClass}">${escapeHtml(confirmLabel)}</button>
          </div>
        </div>
      </div>`;

    const cleanup = (result) => {
      window.removeEventListener('keydown', onKeyDown);
      if (container.parentNode) {
        document.body.removeChild(container);
      }
      resolve(result);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') cleanup(false);
    };

    container.querySelector('[data-confirm-backdrop]')?.addEventListener('click', () => cleanup(false));
    container.querySelector('[data-confirm-cancel]')?.addEventListener('click', () => cleanup(false));
    container.querySelector('[data-confirm-ok]')?.addEventListener('click', () => cleanup(true));

    document.body.appendChild(container);
    window.addEventListener('keydown', onKeyDown);
    container.querySelector('[data-confirm-ok]')?.focus();
  });
}
